import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import './CanvasBackground.scss';
import DeskCanvas from '../DeskCanvas/DeskCanvas';
import Character from '../Character/Character';
import TablePromoSlot from '../PromoSlot/TablePromoSlot';
import CHARACTERS from '../../data/characters';
import { sizeCanvas, cssSize } from '../../utils/canvasSize';

// カメラターゲット（1920×1024 内部座標、顔付近を狙う）
const CAMERA_TARGETS = {
  riku:   { x: 280,  y: 660 },
  ponta:  { x: 760,  y: 420 },
  fuwari: { x: 1160, y: 240 },
  seiji:  { x: 1600, y: 600 },
};

// hidden: true のキャラは描画しない（作業中の一時非表示用）
const VISIBLE_CHARACTERS = CHARACTERS.filter((c) => !c.hidden);

const ZOOM_SCALE = 1.8;
const PORTRAIT_BASE_SCALE = 1.2; // 縦画面デフォルトズーム

function calcViewportInfo() {
  const winW = window.innerWidth;
  const winH = window.innerHeight;
  const targetRatio = 1920 / 1024;
  const windowRatio = winW / winH;

  let canvasW, canvasH;
  if (windowRatio > targetRatio) {
    canvasH = winH;
    canvasW = winH * targetRatio;
  } else {
    canvasW = winW;
    canvasH = winW / targetRatio;
  }

  const offsetX = (winW - canvasW) / 2;
  const offsetY = (winH - canvasH) / 2;
  const isPortrait = winH > winW;

  return { winW, winH, canvasW, canvasH, offsetX, offsetY, isPortrait };
}

function calcDefaultStyle() {
  const { isPortrait } = calcViewportInfo();
  if (!isPortrait) return { transform: 'scale(1)' };

  // 縦画面: キャラクターが画面幅いっぱいに表示されるようズーム
  return {
    transformOrigin: '50% 35%',
    transform: `scale(${PORTRAIT_BASE_SCALE})`,
  };
}

function calcZoomStyle(speakerId) {
  if (!speakerId) return calcDefaultStyle();

  const target = CAMERA_TARGETS[speakerId];
  if (!target) return calcDefaultStyle();

  const { winW, winH, canvasW, canvasH, offsetX, offsetY } = calcViewportInfo();

  // キャラの顔位置（viewport 座標でのピクセル位置）
  const facePx = offsetX + (target.x / 1920) * canvasW;
  const facePy = offsetY + (target.y / 1024) * canvasH;

  // viewport 全体に対するパーセンテージ
  const fxPct = (facePx / winW) * 100;
  const fyPct = (facePy / winH) * 100;

  // 顔位置を画面中央に持ってくる translate
  const tx = 50 - fxPct;
  const ty = 50 - fyPct;

  return {
    transformOrigin: '50% 50%',
    transform: `scale(${ZOOM_SCALE}) translate(${tx}%, ${ty}%)`,
  };
}

const CanvasBackground = ({
  children,
  activeSpeaker,
  sequenceStart = null,
  tablePromo = null,
  tablePromoPlacement,
  promoKeyword,
  onPromoClick,
  paused = false,
}) => {
  const [winSize, setWinSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const canvasRef = useRef(null);
  const sofaCanvasRef = useRef(null);
  const imagesRef = useRef({
    kabe: null,
    sofa: null
  });
  const loadedCount = useRef(0);
  // マスター3プリセットで共有する状態（現在位置・切替フラグ）
  const masterSharedRef = useRef({ x: null });
  const [shouldZoom, setShouldZoom] = useState(false);

  // マスターがテーブル中央で後ろ向きになったか（master_walk の set:{atTable} で立つフラグ）。
  // 一度立ったらその話題の間は下ろさない＝商材は机に置かれたまま。
  const [masterAtTable, setMasterAtTable] = useState(false);

  // 新しい商材を取りに行った（= null になった）ら、置かれる前の状態に戻す
  useEffect(() => {
    if (!tablePromo) setMasterAtTable(false);
  }, [tablePromo]);

  // sharedState は React の外（描画ループ）で書き換わるので、ポーリングで拾う
  useEffect(() => {
    const id = setInterval(() => {
      if (masterSharedRef.current.atTable) setMasterAtTable(true);
    }, 100);
    return () => clearInterval(id);
  }, []);

  // リサイズ時にウィンドウサイズを更新（ズーム再計算用）
  useEffect(() => {
    const onResize = () => setWinSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 発言者が変わるたびに 50% の確率でズームするか決定
  useEffect(() => {
    if (activeSpeaker) {
      setShouldZoom(Math.random() < 0.5);
    } else {
      setShouldZoom(false);
    }
  }, [activeSpeaker]);

  const zoomStyle = useMemo(
    () => shouldZoom ? calcZoomStyle(activeSpeaker) : calcDefaultStyle(),
    [activeSpeaker, winSize, shouldZoom]
  );

  useEffect(() => {
    // 壁紙(kabe)とソファ(sofa)は別 canvas に分ける。
    // その間にマスターのキャラ canvas を挟むことで「sofa の背面」に立たせる。
    const kabeCanvas = canvasRef.current;
    const sofaCanvas = sofaCanvasRef.current;
    const kabeCtx = kabeCanvas.getContext('2d');
    const sofaCtx = sofaCanvas.getContext('2d');

    const imagePaths = {
      kabe: '/img/kabe.png',
      sofa: '/img/sofa.png'
    };

    const resizeCanvas = () => {
      // ビューポート全体に広げる（黒下地を出さない）。
      // 実ピクセルは画面密度に合わせて確保する（スマホでの粗さ対策）
      [kabeCanvas, sofaCanvas].forEach((c) => {
        sizeCanvas(c, window.innerWidth, window.innerHeight);
      });
      drawBackground();
    };

    // cover ロジックでビューポート全体に描画
    const drawCover = (ctx, img, cw, ch) => {
      const imgRatio = img.width / img.height;
      const cvRatio = cw / ch;
      let dw, dh;
      if (cvRatio > imgRatio) {
        dw = cw;
        dh = cw / imgRatio;
      } else {
        dh = ch;
        dw = ch * imgRatio;
      }
      const dx = (cw - dw) / 2;
      const dy = (ch - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
    };

    const drawBackground = () => {
      // 座標計算は CSS ピクセル基準（canvas.width は dpr 倍になっている）
      const { cssW: cw, cssH: ch } = cssSize(kabeCanvas);
      kabeCtx.clearRect(0, 0, cw, ch);
      sofaCtx.clearRect(0, 0, cw, ch);

      // 壁紙を最背面に描画
      if (imagesRef.current.kabe && imagesRef.current.kabe.complete) {
        drawCover(kabeCtx, imagesRef.current.kabe, cw, ch);
      }

      // ソファ（カウンター含む）は前面レイヤーに描画
      if (imagesRef.current.sofa && imagesRef.current.sofa.complete) {
        drawCover(sofaCtx, imagesRef.current.sofa, cw, ch);
      }

      sofaCtx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      sofaCtx.fillRect(0, 0, cw, ch);
    };


    const loadImage = (key, src) => {
      const img = new Image();
      img.onload = () => {
        imagesRef.current[key] = img;
        loadedCount.current++;
        drawBackground(); // 各画像が読み込まれるたびに再描画
        if (loadedCount.current === Object.keys(imagePaths).length) {
          resizeCanvas();
        }
      };
      img.onerror = () => {
        console.error(`Failed to load ${key} image`);
        loadedCount.current++;
        if (loadedCount.current === Object.keys(imagePaths).length) {
          // 画像が読み込めない場合はグラデーション背景
          const { cssW: width, cssH: height } = cssSize(kabeCanvas);
          const gradient = kabeCtx.createLinearGradient(0, 0, width, height);
          gradient.addColorStop(0, '#f5f1e8');
          gradient.addColorStop(1, '#e6d5b8');
          kabeCtx.fillStyle = gradient;
          kabeCtx.fillRect(0, 0, width, height);
        }
      };
      img.src = src;
    };

    // 画像を読み込む
    Object.entries(imagePaths).forEach(([key, path]) => {
      loadImage(key, path);
    });

    // リサイズイベント
    window.addEventListener('resize', resizeCanvas);

    const initCanvas = () => {
      [kabeCanvas, sofaCanvas].forEach((c) => {
        sizeCanvas(c, window.innerWidth, window.innerHeight);
      });
    };

    initCanvas();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <div className="canvas-background">
      <div className="scene-layer" style={zoomStyle}>
        <canvas
          ref={canvasRef}
          className={`background-canvas${shouldZoom && activeSpeaker ? ' blurred' : ''}`}
        />
        {/*
          テーブル上の商材枠。z-index は desk(8) と desk2(10) の間の 9。
          マスターも同じ 9 なので、同値のときは DOM 順で前後が決まる。
          ここ（キャラより前）に置くことで、マスターが商材の手前に立つ。
          マスターがテーブル中央で後ろ向きになってから出す（＝机に置いた体）。
        */}
        <TablePromoSlot
          promo={masterAtTable ? tablePromo : null}
          placementSlug={tablePromoPlacement}
          keyword={promoKeyword}
          onPromoClick={onPromoClick}
        />
        {/* sofa より奥のキャラ（マスター）。sofa canvas に下半身が隠れる */}
        {VISIBLE_CHARACTERS.filter((c) => c.behindSofa).map((c) => (
          <Character
            key={c.id}
            id={c.id}
            displayName={c.displayName}
            basePosition={c.basePosition}
            scale={c.scale}
            placeholderColor={c.placeholderColor}
            parts={c.parts}
            assetDir={c.assetDir}
            isSpeaking={activeSpeaker === c.id}
            zIndex={1}
            // blurOnZoom を立てたキャラだけ、ズーム時に背景と一緒にぼける
            blurred={!!c.blurOnZoom && shouldZoom && !!activeSpeaker}
            bgPosition={c.bgPosition}
            move={c.move}
            timeOrigin={sequenceStart}
            sharedState={masterSharedRef.current}
            paused={paused}
          />
        ))}
        <canvas
          ref={sofaCanvasRef}
          // ソファ・カウンターはズーム時もぼかさない（手前の造作なのでピントを合わせておく）
          className="sofa-canvas"
        />
        {VISIBLE_CHARACTERS.filter((c) => !c.behindSofa).map((c) => (
          <Character
            key={c.id}
            id={c.id}
            displayName={c.displayName}
            basePosition={c.basePosition}
            scale={c.scale}
            placeholderColor={c.placeholderColor}
            parts={c.parts}
            assetDir={c.assetDir}
            isSpeaking={activeSpeaker === c.id}
            zIndex={c.zIndex}
            blurred={!!c.blurOnZoom && shouldZoom && !!activeSpeaker}
            bgPosition={c.bgPosition}
            move={c.move}
            timeOrigin={sequenceStart}
            sharedState={masterSharedRef.current}
            paused={paused}
          />
        ))}
        <DeskCanvas />
      </div>
      <div className="canvas-content">
        {children}
      </div>
    </div>
  );
};

export default CanvasBackground;
